import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createTenant } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  Building2,
  Save,
  AlertCircle,
  Phone,
  Clock,
  Mail,
  FileText,
  Star,
  Trash2,
  Megaphone,
  Bell,
  AlertTriangle,
  Info,
  Calendar,
  Upload,
  HelpCircle,
} from "lucide-react";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";

// Tooltip component (reusable)
const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: string;
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

interface TenantFormData {
  companyName: string;
  description: string;
  category: string;
  status: "ACTIVE" | "INACTIVE" | "PENDING" | "SUSPENDED";
  unitNumber: string;
  floor: string;
  building: string;
  businessHours: string;
  services: string;
  contactPerson: string;
  email: string;
  phone: string;
  website: string;
  isVerified: boolean;
  isFeatured: boolean;
}

interface FormErrors {
  companyName?: string;
  email?: string;
  phone?: string;
  website?: string;
  submit?: string;
}

const TENANT_CATEGORIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "Education",
  "Retail",
  "Food & Beverage",
  "Professional Services",
  "Manufacturing",
  "Real Estate",
  "Entertainment",
  "Other",
];

const TENANT_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "PENDING", label: "Pending" },
  { value: "SUSPENDED", label: "Suspended" },
];

export default function AddTenantPage() {
  useRequireSectionVisible("showTenantBusiness");
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState<TenantFormData>({
    companyName: "",
    description: "",
    category: "",
    status: "ACTIVE",
    unitNumber: "",
    floor: "",
    building: "",
    businessHours: "",
    services: "",
    contactPerson: "",
    email: "",
    phone: "",
    website: "",
    isVerified: false,
    isFeatured: false,
  });

  const handleChange = (
    field: keyof TenantFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.phone &&
      !/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone?.replace(/[\s\-\(\)]/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      newErrors.website = "Website must start with http:// or https://";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedLocation) {
      setErrors({ submit: "Please select a location first" });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await createTenant({
        companyName: formData.companyName,
        description: formData.description,
        category: formData.category,
        status: formData.status,
        unitNumber: formData.unitNumber,
        floor: formData.floor,
        building: formData.building,
        businessHours: formData.businessHours,
        services: formData.services
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        contactPerson: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        isVerified: formData.isVerified,
        isFeatured: formData.isFeatured,
        locationId: selectedLocation.id,
      });

      navigate("/tenants");
    } catch (error: any) {
      console.error("Error creating tenant:", error);
      setErrors({
        submit: error.message || "Failed to create tenant. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/tenants");
  };

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="tenant-form"
        disabled={isLoading || !formData.companyName.trim()}
        icon={Save}
      >
        {isLoading ? "Submitting..." : "Submit"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack} disabled={isLoading}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Contact Information" hideOnMobile>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Contact Person
            </label>
            <input
              type="text"
              value={formData.contactPerson}
              onChange={(e) => handleChange("contactPerson", e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
              placeholder="Phone"
            />
          </div>
        </div>
      </SidebarSection>

      <SidebarSection title="Additional Options" hideOnMobile>
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-2 bg-white border border-slate-100 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.isVerified}
              onChange={(e) => handleChange("isVerified", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-700">Verified</span>
          </label>
          <label className="flex items-center gap-3 p-2 bg-white border border-slate-100 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={formData.isFeatured}
              onChange={(e) => handleChange("isFeatured", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-700">Featured</span>
          </label>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Add Tenant"
      subtitle={`Location: ${selectedLocation?.name || "No location selected"}`}
      icon={Building2}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="tenant-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Full-width sections */}
        <div className="space-y-6 mb-6">
          {/* Primary Input Field */}
          <div>
            <h3 className="text-lg font-medium text-content-text mb-4">
              What are you listing?
            </h3>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) =>
                handleChange("companyName", e.target.value)
              }
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder ${errors.companyName
                ? "border-destructive bg-destructive/10"
                : ""
                }`}
              placeholder="e.g., SpaceX Mars Operations, Virgin Galactic Tours"
              required
            />
            {errors.companyName && (
              <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.companyName}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <h3 className="text-lg font-medium text-content-text mb-4">
              Description
            </h3>
            <textarea
              value={formData.description}
              onChange={(e) =>
                handleChange("description", e.target.value)
              }
              rows={4}
              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
              placeholder="Provide detailed description, services offered, any unique features or special information..."
            />
            <p className="text-sm text-content-text-muted mt-2">
              {formData.description.length}/500 characters
            </p>
          </div>
        </div>

        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Category and Status Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    handleChange("category", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border"
                >
                  <option value="">Select a category</option>
                  {TENANT_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    handleChange("status", e.target.value as any)
                  }
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border"
                >
                  {TENANT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location Details */}
            <div>
              <h3 className="text-lg font-medium text-content-text mb-4">
                Specific Location
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Unit #
                  </label>
                  <input
                    type="text"
                    value={formData.unitNumber}
                    onChange={(e) =>
                      handleChange("unitNumber", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Unit #"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Floor
                  </label>
                  <input
                    type="text"
                    value={formData.floor}
                    onChange={(e) =>
                      handleChange("floor", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Floor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Building
                  </label>
                  <input
                    type="text"
                    value={formData.building}
                    onChange={(e) =>
                      handleChange("building", e.target.value)
                    }
                    className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                    placeholder="Building"
                  />
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div>
              <h3 className="text-lg font-medium text-content-text mb-4">
                Business Hours
              </h3>
              <input
                type="text"
                value={formData.businessHours}
                onChange={(e) =>
                  handleChange("businessHours", e.target.value)
                }
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                placeholder="e.g., Mon-Fri 9AM-6PM, Sat 10AM-4PM"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Services */}
            <div>
              <h3 className="text-lg font-medium text-content-text mb-4">
                Services & Offerings
              </h3>
              <textarea
                value={formData.services}
                onChange={(e) =>
                  handleChange("services", e.target.value)
                }
                rows={8}
                className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors font-mono text-sm bg-input-bg text-input-text border-input-border placeholder-input-placeholder"
                placeholder="List main services, API keys, or other detailed information (e.g., Space Tourism, Mars Exploration, Commercial Space Travel)"
                style={{ minHeight: "200px", resize: "vertical" }}
              />
              <p className="text-sm text-content-text-muted mt-2">
                {formData.services.length}/2000 characters
              </p>
            </div>
          </div>
        </div>

        {/* Error message */}
        {errors.submit && (
          <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </IndividualEditPageLayout>
  );
}
